import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Menu, X, ArrowRight, Sun, Moon } from "lucide-react";
import { iconByName } from "@/lib/icons";
import { CAPABILITIES, BRAND } from "@/data/content";
import { INDUSTRY_LIST } from "@/data/industries";
import { serviceSlug } from "@/data/services";
import { scrollToId, getLenis } from "@/lib/scroll";
import { getTheme, setTheme } from "@/lib/theme";

const ThemeToggle = ({ testid }) => {
    const [theme, setThemeState] = useState(getTheme());

    useEffect(() => {
        const onTheme = (e) => setThemeState(e.detail);
        window.addEventListener("vtheme", onTheme);
        return () => window.removeEventListener("vtheme", onTheme);
    }, []);

    const toggle = () => {
        const next = theme === "dark" ? "light" : "dark";
        setTheme(next);
    };

    return (
        <button
            type="button"
            data-testid={testid}
            onClick={toggle}
            aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            className="flex h-11 w-11 items-center justify-center rounded-full border t-border t-soft transition-colors t-hover-accent t-hover-accent-border"
        >
            {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
        </button>
    );
};

const MENUS = [
    {
        label: "Capabilities",
        testid: "nav-capabilities-menu",
        wide: true,
        items: CAPABILITIES.map((c) => ({
            name: c.title,
            icon: c.icon,
            subs: c.points,
            to: `/services/${serviceSlug(c.title)}`,
        })).concat([
            {
                name: "Microsoft 365",
                icon: "Laptop",
                subs: ["Identity & Access", "Endpoint Management", "Collaboration", "Security & Compliance"],
                to: "/services/microsoft-365",
            },
        ]),
    },
    {
        label: "Industries",
        testid: "nav-industries-menu",
        items: INDUSTRY_LIST.map((i) => ({ name: i.name, icon: i.icon, to: `/industries/${i.slug}` })),
    },
];

const LINKS = [
    { label: "Methodology", hash: "methodology", testid: "nav-methodology-link" },
    { label: "Insights", path: "/insights", testid: "nav-insights-link" },
    { label: "About", path: "/about", testid: "nav-about-link" },
];

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [theme, setThemeState] = useState(getTheme());
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onTheme = (e) => setThemeState(e.detail);
        window.addEventListener("vtheme", onTheme);
        return () => window.removeEventListener("vtheme", onTheme);
    }, []);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const lenis = getLenis();
        if (open) {
            lenis?.stop();
            document.body.style.overflow = "hidden";
        } else {
            lenis?.start();
            document.body.style.overflow = "";
        }
    }, [open]);

    const goHash = (hash) => (e) => {
        e.preventDefault();
        setOpen(false);
        if (location.pathname === "/") {
            scrollToId(hash);
        } else {
            navigate(`/#${hash}`);
        }
    };

    const goCapabilities = goHash("capabilities");

    return (
        <>
            <a href="#main-content" className="skip-link">
                Skip to content
            </a>
            <header
                className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${
                    scrolled || theme === "light" ? "border-b t-border t-navbg backdrop-blur-xl" : "border-b border-transparent bg-transparent"
                }`}
            >
                <nav className="container-x flex h-[96px] items-center justify-between" aria-label="Primary">
                    <Link to="/" data-testid="nav-logo" className="flex items-center gap-3" aria-label="Varasun Technologies home">
                        <img
                            src="/logo-main.webp"
                            alt="Varasun Technologies"
                            className="h-14 w-auto rounded-xl border t-border shadow-sm transition-transform duration-300 hover:scale-[1.04] md:h-20"
                        />
                    </Link>

                    <div className="hidden items-center gap-1 lg:flex">
                        {MENUS.map((menu) => (
                            <div key={menu.label} className="group relative">
                                <button
                                    data-testid={menu.testid}
                                    onClick={menu.label === "Capabilities" ? goCapabilities : goHash("industries")}
                                    className="flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium t-soft transition-colors t-hover-heading"
                                    aria-haspopup="true"
                                >
                                    {menu.label}
                                    <ChevronDown size={14} className="transition-transform duration-300 group-hover:rotate-180" />
                                </button>
                                <div
                                    className={`pointer-events-none absolute left-1/2 top-full z-50 -translate-x-1/2 translate-y-3 pt-3 opacity-0 transition-[opacity,transform] duration-300 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:translate-y-0 group-focus-within:opacity-100 ${
                                        menu.wide ? "w-[38rem]" : "w-[28rem]"
                                    }`}
                                >
                                    <div
                                        className={`overflow-hidden rounded-2xl border t-border t-popover p-3 shadow-[0_32px_80px_-24px_rgba(0,0,0,0.8)] backdrop-blur-xl ${
                                            menu.wide ? "grid grid-cols-2 gap-1" : ""
                                        }`}
                                    >
                                        {menu.items.map((item) => {
                                            const Icon = iconByName(item.icon);
                                            const inner = (
                                                <>
                                                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-electric/25 bg-electric/10 text-electric">
                                                        <Icon size={17} />
                                                    </span>
                                                    <span>
                                                        <span className="block font-display text-sm font-semibold t-heading">{item.name}</span>
                                                        <span className="mt-0.5 block font-mono text-[10px] uppercase leading-relaxed tracking-wider t-faint">
                                                            {item.subs ? item.subs.join(" · ") : item.desc}
                                                        </span>
                                                    </span>
                                                </>
                                            );
                                            const cls = "flex items-start gap-4 rounded-xl px-4 py-3 transition-colors t-hoverbg";
                                            const tid = `nav-menu-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
                                            return item.to ? (
                                                <Link key={item.name} to={item.to} data-testid={tid} className={cls}>
                                                    {inner}
                                                </Link>
                                            ) : (
                                                <a key={item.name} href="#capabilities" onClick={goCapabilities} data-testid={tid} className={cls}>
                                                    {inner}
                                                </a>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {LINKS.map((l) =>
                            l.path ? (
                                <Link
                                    key={l.label}
                                    to={l.path}
                                    data-testid={l.testid}
                                    className="rounded-full px-4 py-2 text-sm font-medium t-soft transition-colors t-hover-heading"
                                >
                                    {l.label}
                                </Link>
                            ) : (
                                <a
                                    key={l.label}
                                    href={`#${l.hash}`}
                                    onClick={goHash(l.hash)}
                                    data-testid={l.testid}
                                    className="rounded-full px-4 py-2 text-sm font-medium t-soft transition-colors t-hover-heading"
                                >
                                    {l.label}
                                </a>
                            )
                        )}
                    </div>

                    <div className="hidden items-center gap-3 lg:flex">
                        <ThemeToggle testid="theme-toggle" />
                        <Link to="/contact" data-testid="nav-talk-to-expert-btn" className="btn-primary !px-6 !py-2.5">
                            Talk to an Expert
                            <ArrowRight size={15} />
                        </Link>
                    </div>

                    <div className="flex items-center gap-2.5 lg:hidden">
                        <ThemeToggle testid="theme-toggle-mobile" />
                        <button
                            data-testid="nav-mobile-toggle"
                            className="flex h-11 w-11 items-center justify-center rounded-full border t-border t-heading"
                            onClick={() => setOpen(true)}
                            aria-label="Open menu"
                            aria-expanded={open}
                        >
                            <Menu size={20} />
                        </button>
                    </div>
                </nav>
            </header>

            <div
                data-testid="nav-mobile-menu"
                className={`fixed inset-0 z-[60] flex flex-col t-bg-solid transition-[opacity,visibility] duration-300 lg:hidden ${
                    open ? "visible opacity-100" : "invisible opacity-0"
                }`}
                role="dialog"
                aria-modal="true"
            >
                <div className="container-x flex h-[96px] items-center justify-between">
                    <Link to="/" onClick={() => setOpen(false)} data-testid="nav-mobile-logo" aria-label="Varasun Technologies home">
                        <img
                            src="/logo-main.webp"
                            alt="Varasun Technologies"
                            className="h-14 w-auto rounded-xl border t-border shadow-sm"
                        />
                    </Link>
                    <div className="flex items-center gap-2.5">
                        <ThemeToggle testid="theme-toggle-mobile-menu" />
                        <button
                            data-testid="nav-mobile-close"
                            className="flex h-11 w-11 items-center justify-center rounded-full border t-border t-heading"
                            onClick={() => setOpen(false)}
                            aria-label="Close menu"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>
                <div className="container-x flex flex-1 flex-col gap-1 overflow-y-auto py-8">
                    {[
                        { label: "Capabilities", action: goCapabilities },
                        { label: "Industries", action: goHash("industries") },
                        ...LINKS.filter((l) => l.hash).map((l) => ({ label: l.label, action: goHash(l.hash) })),
                    ].map((item, i) => (
                        <button
                            key={item.label}
                            data-testid={`nav-mobile-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={item.action}
                            className="border-b t-border py-5 text-left font-display text-2xl font-semibold t-heading transition-colors hover:text-electric"
                            style={{ transitionDelay: `${i * 30}ms` }}
                        >
                            {item.label}
                        </button>
                    ))}
                    <Link
                        to="/insights"
                        data-testid="nav-mobile-insights"
                        className="border-b t-border py-5 font-display text-2xl font-semibold t-heading transition-colors hover:text-electric"
                    >
                        Insights
                    </Link>
                    <Link
                        to="/about"
                        data-testid="nav-mobile-about"
                        className="border-b t-border py-5 font-display text-2xl font-semibold t-heading transition-colors hover:text-electric"
                    >
                        About
                    </Link>
                    <Link to="/contact" data-testid="nav-mobile-cta" className="btn-primary mt-8 w-full">
                        Talk to an Expert
                        <ArrowRight size={16} />
                    </Link>
                    <p className="mt-auto pt-10 font-mono text-[11px] uppercase tracking-[0.25em] t-faint">{BRAND.tagline}</p>
                </div>
            </div>
        </>
    );
};

export default Navbar;
